import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCategoriesTree } from "../../../hooks/category";
import gridIcon from "../../../dist/icons/grid.svg";
import ModalBox from "../modal_box";

// SVG arrow icon replacing Ant Design RightOutlined
const ChevronRight = ({ className }) => (
    <svg className={className || "w-3 h-3"} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

function CategoryItem({ category, onClick }) {
    const image = category.image != null
        ? category.image
        : 'https://kartinkin.com/uploads/posts/2021-07/thumbs/1626123851_61-kartinkin-com-p-svetlo-serii-fon-krasivo-63.jpg';
    return (
        <div 
            className="flex-shrink-0 flex items-center gap-2 bg-slate-50 hover:bg-blue-50 border border-slate-100 hover:border-blue-100 px-3 sm:px-4 py-2 rounded-xl cursor-pointer transition-all shadow-sm active:scale-95"
            onClick={onClick}
        >
            <img className="rounded-full w-6 h-6 sm:w-7 sm:h-7 object-cover border border-slate-100 shadow-sm" src={image} alt={category.name} />
            <span className="text-xs sm:text-sm font-semibold text-slate-700 hover:text-blue-600 whitespace-nowrap">
                {category.name}
            </span>
        </div>
    )
}

const CategorySlider = () => {
    const categories = useCategoriesTree();
    const navigate = useNavigate();

    return (
        <div className="flex flex-row items-center gap-2 sm:gap-3 w-full my-3 sm:my-4 px-2 sm:px-0">
            {/* Mega Menu Categories Button */}
            <div className="flex-shrink-0">
                <ModalBox
                    modal={<CategoriesTreeModal categories={categories} />}
                >
                    <div
                        className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white flex gap-2 px-3 sm:px-4 items-center justify-center rounded-xl cursor-pointer transition shadow-sm h-10 sm:h-11 active:scale-95"
                    >
                        <img src={gridIcon} alt="Grid" className="w-5 h-5 brightness-0 invert" />
                        <span className="text-sm font-bold hidden sm:inline">Все категории</span>
                    </div>
                </ModalBox>
            </div>

            {/* Horizontal Scrolling Categories */}
            <div className="flex-1 overflow-x-auto scrollbar-hide flex items-center gap-2 sm:gap-3 py-1">
                {categories.map((category) => (
                    <CategoryItem
                        key={category.id}
                        category={category}
                        onClick={() => navigate('/category/' + category.id)}
                    />
                ))}
            </div>
        </div>
    );
};

function CategoriesTreeModal({ categories }) {
    const [selectedCatId, setSelectedCatId] = useState(categories[0]?.id);
    const navigate = useNavigate();

    const selectedCategory = categories.find(cat => cat.id === selectedCatId);
    const subcategories = selectedCategory?.children || [];

    useEffect(() => {
        if (categories?.length > 0 && !selectedCatId) {
            setSelectedCatId(categories[0].id);
        }
    }, [categories, selectedCatId]);

    return (
        <div
            id="mega-menu-full-dropdown"
            className="bg-white sm:border sm:border-slate-100 z-50 sm:rounded-2xl w-full sm:w-[600px] max-w-full sm:shadow-2xl text-sm transition-all duration-300 overflow-hidden"
            style={{ height: "400px" }}
        >
            <div className="flex flex-row h-full">
                {/* Left Pane: Parent Categories */}
                <div className="w-[40%] sm:w-[45%] border-r border-slate-100 overflow-y-auto p-1.5 sm:p-2 space-y-0.5 sm:space-y-1">
                    {categories.map((category) => {
                        const isHovered = selectedCatId === category.id;
                        return (
                            <div
                                key={category.id}
                                onMouseEnter={() => setSelectedCatId(category.id)}
                                onClick={() => {
                                    setSelectedCatId(category.id);
                                    // On mobile, just select — don't navigate
                                    if (window.innerWidth >= 640) {
                                        navigate('/category/' + category.id);
                                    }
                                }}
                                className={`flex flex-row justify-between items-center w-full py-2 px-2 sm:px-3 rounded-xl cursor-pointer transition-all ${
                                    isHovered 
                                        ? "bg-blue-50 text-blue-600 font-semibold" 
                                        : "hover:bg-slate-50 text-slate-700"
                                }`}
                            >
                                <div className="flex flex-row items-center gap-2 sm:gap-2.5 min-w-0">
                                    <img src={category.image} className="w-6 h-6 sm:w-7 sm:h-7 rounded-full object-cover border border-slate-100 shadow-sm flex-shrink-0" alt="" />
                                    <span className="text-[11px] sm:text-xs truncate">{category.name}</span>
                                </div>
                                <ChevronRight className={`w-3 h-3 flex-shrink-0 ${isHovered ? "text-blue-500" : "text-slate-350"}`} />
                            </div>
                        )
                    })}
                </div>

                {/* Right Pane: Subcategories */}
                <div className="w-[60%] sm:w-[55%] overflow-y-auto p-3 sm:p-4 space-y-3 bg-slate-50/50">
                    {selectedCategory && (
                        <div className="space-y-3">
                            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                                <span className="text-[12px] sm:text-[13px] font-bold text-slate-800 uppercase tracking-wider truncate">{selectedCategory.name}</span>
                                <Link to={`/category/${selectedCategory.id}`} className="text-[10px] sm:text-xs font-semibold text-blue-600 hover:underline whitespace-nowrap ml-2">
                                    Все →
                                </Link>
                            </div>
                            
                            {subcategories.length === 0 ? (
                                <div className="text-xs text-slate-400 font-semibold py-4 text-center">
                                    Нет подкатегорий
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 gap-1 sm:gap-2">
                                    {subcategories.map((sub) => (
                                        <Link
                                            key={sub.id}
                                            to={`/category/${sub.id}`}
                                            className="text-[11px] sm:text-xs text-slate-600 hover:text-blue-600 font-semibold py-1.5 px-2 sm:px-2.5 rounded-lg hover:bg-white transition active:bg-blue-50"
                                        >
                                            {sub.name}
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CategorySlider;
